import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { useThreadLoading } from '../../../contexts/ThreadLoadingContext';
import { useNewThread } from '../../../contexts/NewThreadContext';
import { useFormCapture } from '../../../contexts/FormCaptureContext';
import { ReactComponent as PinboardIcon } from '../../../icons/pinboard.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/tabs/campaign.svg';
import { ReactComponent as TabCloseIcon } from '../../../icons/tabs/tab-close.svg';
import CollapsedRevealer from './CollapsedRevealer';
import {
  UIDSLComponent,
  UIDSLComponentProps,
  UIDSLComponentType,
  UpdateThreadCanvasViewRequest,
  ThreadLayoutType,
} from '@dreamai/types';
import { UIUpdate } from './UIComponentRenderer';
import { getAppLayoutConfig } from './appLayoutConfig';
import AppLayoutAssetModal from './AppLayoutAssetModal';

// Define interfaces for CanvasAsset and CanvasAssetComponent based on project structure
interface CanvasAssetComponent {
  id: string;
  component_type: string;
  props: Record<string, any>;
}

interface CanvasAsset {
  id: string;
  thread_id: string;
  created_at: string;
  title?: string;
  components: CanvasAssetComponent[];
  pinned?: boolean;
}
import { Comments } from './Comments';
import { useComments } from '../../../contexts/CommentsContext';
import ViewSwitcher from './ViewSwitcher';
import api from '../../../api';
import './canvas-assets.scss';

interface CanvasAssetsProps {
  threadId: string;
  layoutType?: ThreadLayoutType;
  discussingObject?: string;
  discussingObjectMatch?: Record<string, any>;
  onHasAssets?: (hasAssets: boolean) => void;
}

type ViewMode = 'canvas' | 'dreambox';

const CanvasAssets: React.FC<CanvasAssetsProps> = ({
  threadId,
  layoutType = 'tab',
  discussingObject,
  discussingObjectMatch,
  onHasAssets,
}) => {
  const [assets, setAssets] = useState<CanvasAsset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>('canvas');
  const [lastCanvasViewTime, setLastCanvasViewTime] = useState<number>(
    Date.now(),
  );
  const [hasNewCanvasAssets, setHasNewCanvasAssets] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    assetId: string | null;
  }>({ visible: false, x: 0, y: 0, assetId: null });
  const [appLayoutModalOpen, setAppLayoutModalOpen] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const canvasContentRef = useRef<HTMLDivElement>(null);
  const canvasAssetsRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Get app layout config for the discussing object type
  const appLayoutConfig = getAppLayoutConfig(discussingObject);
  const isAppLayout = layoutType === 'app' && !!appLayoutConfig;

  // Dynamic tabs from app layout components (e.g. CampaignWrapper tells us tabs after loading)
  const [dynamicTabs, setDynamicTabs] = useState<
    { id: string; label: string }[]
  >([]);

  // Callback for components to set their tabs dynamically
  const handleTabsReady = useCallback(
    (tabs: { id: string; label: string }[]) => {
      setDynamicTabs(tabs);
    },
    [],
  );

  // Reset dynamic tabs when thread or discussing object changes
  useEffect(() => {
    setDynamicTabs([]);
  }, [threadId, discussingObject]);

  // Use dynamic tabs if available, otherwise fall back to config tabs
  const effectiveTabs =
    dynamicTabs.length > 0 ? dynamicTabs : appLayoutConfig?.tabs || [];

  // Check if we should show the header
  const hasAppTabs = effectiveTabs.length > 0;
  const showHeader = isAppLayout ? hasAppTabs : assets.length > 0;

  // Comments integration
  const { comments, fetchCommentsForAsset } = useComments();

  // URL search params for tab persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Track when user switches to canvas view to reset new asset flag
  useEffect(() => {
    if (viewMode === 'canvas') {
      setLastCanvasViewTime(Date.now());
      setHasNewCanvasAssets(false);
    }
  }, [viewMode]);

  // Check for new canvas assets since last canvas view
  useEffect(() => {
    if (assets.length > 0 && viewMode === 'dreambox') {
      const hasNewAssets = assets.some((asset) => {
        const assetTime = new Date(asset.created_at).getTime();
        return assetTime > lastCanvasViewTime;
      });
      setHasNewCanvasAssets(hasNewAssets);
    }
  }, [assets, lastCanvasViewTime, viewMode]);

  // Reopen modal when new assets arrive in app layout mode
  useEffect(() => {
    if (isAppLayout && assets.length > 0) {
      setAppLayoutModalOpen(true);
    }
  }, [isAppLayout, assets.length]);

  // Notify parent when there's content to display (assets or app layout content)
  useEffect(() => {
    const hasAppContent = isAppLayout && hasAppTabs;
    const hasContent = assets.length > 0 || hasAppContent;
    onHasAssets?.(hasContent);
  }, [assets.length, isAppLayout, hasAppTabs, onHasAssets]);

  // Animation states
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [animationStage, setAnimationStage] = useState<
    'fadeOut' | 'fadeIn' | 'none'
  >('none');
  const [prevAssetId, setPrevAssetId] = useState<string | null>(null);

  // Animation timing constants
  const FADE_OUT_DURATION = 50;
  const FADE_IN_DURATION = 50;

  // Function to scroll tab into view
  const scrollTabIntoView = useCallback((assetId: string) => {
    const tabElement = tabsRef.current[assetId];
    if (tabElement) {
      tabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, []);

  // Function to handle asset transitions with animation
  const animateAssetTransition = useCallback(
    (newAssetId: string, currentAssetId: string | null) => {
      // Animation disabled for now
      setSelectedAssetId(newAssetId);
      scrollTabIntoView(newAssetId);

      // if (currentAssetId) {
      //   // If we have an existing asset, do a fade out first
      //   setPrevAssetId(currentAssetId);
      //   setIsAnimating(true);
      //   setAnimationStage('fadeOut');

      //   // After fadeOut completes, switch to the new asset and start fadeIn
      //   setTimeout(() => {
      //     setSelectedAssetId(newAssetId);
      //     setAnimationStage('fadeIn');

      //     // Scroll the new tab into view
      //     scrollTabIntoView(newAssetId);

      //     // After fadeIn completes, reset animation states
      //     setTimeout(() => {
      //       setIsAnimating(false);
      //       setAnimationStage('none');
      //       setPrevAssetId(null);
      //     }, FADE_IN_DURATION);
      //   }, FADE_OUT_DURATION);
      // } else {
      //   // No existing asset, just set the new one directly
      //   setSelectedAssetId(newAssetId);
      //   // Scroll the tab into view
      //   scrollTabIntoView(newAssetId);
      // }
    },
    [scrollTabIntoView],
  );

  const {
    registerCanvasAssetCreatedCallback,
    unregisterCanvasAssetCreatedCallback,
    registerCanvasAssetRemovedCallback,
    unregisterCanvasAssetRemovedCallback,
    registerConfettiCallback,
    unregisterConfettiCallback,
  } = useThreadLoading();
  const { newThreadId } = useNewThread();
  const { setActiveForm } = useFormCapture();

  // Use the appropriate thread ID (existing or new)
  const effectiveThreadId = threadId || newThreadId;

  const { getAccessTokenSilently } = useAuth0();

  // Batch delete canvas assets
  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target as Node)
      ) {
        setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [contextMenu.visible]);

  const deleteCanvasAssets = useCallback(
    async (assetIds: string[]) => {
      const deletableAssets = assets.filter((asset) =>
        assetIds.includes(asset.id),
      );

      if (deletableAssets.length === 0) {
        return { success: false, error: 'No deletable assets found' };
      }

      const deletableIds = deletableAssets.map((a) => a.id);

      // Track deleted assets to prevent them from being restored by fetch
      deletableIds.forEach((id) => deletedAssetIdsRef.current.add(id));

      // Optimistically remove from UI
      const previousAssets = [...assets];
      const previousSelectedAssetId = selectedAssetId;
      setAssets((prev) => prev.filter((a) => !deletableIds.includes(a.id)));

      // If the selected asset is being deleted, switch to another tab
      if (selectedAssetId && deletableIds.includes(selectedAssetId)) {
        const remainingAssets = assets.filter(
          (a) => !deletableIds.includes(a.id),
        );
        if (remainingAssets.length > 0) {
          const newAssetId = remainingAssets[0].id;
          setSelectedAssetId(newAssetId);
          // Update URL to reflect the new selected asset
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('asset_id', newAssetId);
            return newParams;
          });
        } else {
          setSelectedAssetId(null);
          // Remove asset_id from URL when no assets left
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('asset_id');
            return newParams;
          });
        }
      }

      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.delete('/canvas-assets', {
          data: { assetIds: deletableIds },
        });

        if (!response.data.success) {
          throw new Error(response.data.error || 'Failed to delete assets');
        }

        return { success: true, deletedIds: deletableIds };
      } catch (error: any) {
        console.error('Error deleting canvas assets:', error);
        // Rollback on error - restore both assets and selected tab
        setAssets(previousAssets);
        setSelectedAssetId(previousSelectedAssetId);
        return {
          success: false,
          error: error.response?.data?.error || 'Failed to delete assets',
        };
      }
    },
    [assets, selectedAssetId, getAccessTokenSilently, setSearchParams],
  );

  // Use refs to store current values to avoid dependency issues
  const effectiveThreadIdRef = useRef(effectiveThreadId);
  const assetsRef = useRef(assets);
  const selectedAssetIdRef = useRef(selectedAssetId);
  const deletedAssetIdsRef = useRef<Set<string>>(new Set());
  const searchParamsRef = useRef(searchParams);
  const switchAssetItemRef = useRef<(newAssetId: string) => void>(() => {});

  // Update refs when values change
  useEffect(() => {
    effectiveThreadIdRef.current = effectiveThreadId;
  }, [effectiveThreadId]);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  useEffect(() => {
    selectedAssetIdRef.current = selectedAssetId;
  }, [selectedAssetId]);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  // This ref is updated after switchAssetItem is defined below

  // Generate a unique callback ID for this component instance
  const callbackIdRef = useRef(
    `canvas-assets-component-${Math.random().toString(36).substr(2, 9)}`,
  );

  // Function to update the thread's visual description
  const updateThreadViewVisualDescription = useCallback(
    async (visualDescription: string) => {
      if (!effectiveThreadId || !selectedAssetId) return;

      try {
        const token = await getAccessTokenSilently();
        const requestBody: UpdateThreadCanvasViewRequest = {
          threadId: effectiveThreadId,
          canvasAssetId: selectedAssetId,
          visualDescription,
        };

        await api.put('/canvas-assets/thread-view', requestBody, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        console.log('Updated thread visual description:', visualDescription);
      } catch (error) {
        console.error('Error updating thread visual description:', error);
      }
    },
    [effectiveThreadId, selectedAssetId, getAccessTokenSilently],
  );

  // Function to update the thread's current canvas view in the backend
  const updateThreadCanvasView = useCallback(
    async (assetId: string) => {
      if (!effectiveThreadId) return;

      try {
        const token = await getAccessTokenSilently();
        const payload: UpdateThreadCanvasViewRequest = {
          threadId: effectiveThreadId,
          canvasAssetId: assetId,
          // We could also add a visual description here if needed
          visualDescription: `Showing the main canvas item`,
        };

        await api.put('/canvas-assets/thread-view', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // We don't need to do anything with the response
        // If it fails, we still want the UI to update
      } catch (error) {
        // Log the error but don't prevent the UI from updating
        console.error('Error updating thread canvas view:', error);
      }
    },
    [effectiveThreadId, getAccessTokenSilently],
  );

  // Combined function to switch asset items - handles both animation and API update
  const switchAssetItem = useCallback(
    (newAssetId: string) => {
      // Don't switch if we don't have a thread ID yet
      if (!effectiveThreadId) {
        return;
      }

      // Don't switch if already on this asset (prevents loops)
      if (selectedAssetIdRef.current === newAssetId) {
        return;
      }

      // Don't switch to deleted assets
      if (deletedAssetIdsRef.current.has(newAssetId)) {
        return;
      }

      // Clear any active form for this thread when switching assets
      if (effectiveThreadId) {
        setActiveForm(effectiveThreadId, null);
      }

      // Start the animation using ref to avoid dependency cycle
      animateAssetTransition(newAssetId, selectedAssetIdRef.current);

      // Update the backend (don't wait for it to complete)
      updateThreadCanvasView(newAssetId);

      // Only update URL parameter if thread_id is in the URL
      // This prevents triggering NewThreadContext clearing when on new=true pages
      // Use ref to get current searchParams value to avoid stale closure
      const urlThreadId = searchParamsRef.current.get('thread_id');
      if (urlThreadId) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('asset_id', newAssetId);
          return newParams;
        });
      }
    },
    [
      animateAssetTransition,
      updateThreadCanvasView,
      effectiveThreadId,
      setActiveForm,
      setSearchParams,
    ],
  );

  // Keep ref updated with latest switchAssetItem
  useEffect(() => {
    switchAssetItemRef.current = switchAssetItem;
  }, [switchAssetItem]);

  // Fetch all canvas assets for the thread
  const fetchCanvasAssets = useCallback(async () => {
    if (!effectiveThreadId) return;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get(
        `/canvas-assets/thread/${effectiveThreadId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const fetchedAssets = response.data.assets || [];

      // Sort assets: pinned first (oldest pinned first), then unpinned (oldest first)
      const sortedAssets = fetchedAssets.sort(
        (a: CanvasAsset, b: CanvasAsset) => {
          // If one is pinned and the other isn't, pinned comes first
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;

          // If both have same pinned status, sort by created_at (oldest first)
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        },
      );

      // Filter out any assets that were just deleted (backend might not have processed yet)
      const filteredAssets = sortedAssets.filter(
        (a: CanvasAsset) => !deletedAssetIdsRef.current.has(a.id),
      );

      setAssets(filteredAssets);

      // Check URL parameter for asset selection
      const urlAssetId = searchParams.get('asset_id');

      if (
        urlAssetId &&
        filteredAssets.some((a: CanvasAsset) => a.id === urlAssetId)
      ) {
        // URL has valid asset_id, use it
        setSelectedAssetId(urlAssetId);
        updateThreadCanvasView(urlAssetId);
      } else if (filteredAssets.length > 0 && !selectedAssetId) {
        // No URL param or invalid, select first asset
        const newAssetId = filteredAssets[0].id;
        setSelectedAssetId(newAssetId);
        updateThreadCanvasView(newAssetId);
        // Set URL parameter
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('asset_id', newAssetId);
          return newParams;
        });
      }
    } catch (error) {
      console.error('Error fetching canvas assets:', error);
    } finally {
      setLoading(false);
    }
  }, [
    effectiveThreadId,
    // Removed selectedAssetId and searchParams to prevent infinite loop
    // These are read from state/URL but don't need to trigger re-fetch
    updateThreadCanvasView,
    getAccessTokenSilently,
    setSearchParams,
  ]);

  // Handle new canvas asset created event
  const handleCanvasAssetCreated = useCallback(
    async (threadId: string, data: { canvasAssetId: string }) => {
      if (threadId !== effectiveThreadIdRef.current) {
        return;
      }

      // Check if asset already exists to prevent duplicates
      if (assetsRef.current.some((asset) => asset.id === data.canvasAssetId)) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(`/canvas-assets/${data.canvasAssetId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newAsset = response.data.asset;

        setAssets((prevAssets) => {
          // Don't add if it's in the deleted list
          if (deletedAssetIdsRef.current.has(newAsset.id)) {
            return prevAssets;
          }

          // Double-check for duplicates in case of race conditions
          if (prevAssets.some((asset) => asset.id === newAsset.id)) {
            return prevAssets;
          }

          // Add at the end to match the "oldest first" sorting order
          return [...prevAssets, newAsset];
        });

        // Switch to the new asset (handles both animation and backend update)
        // Only switch if not deleted
        // Use ref to always get the latest switchAssetItem function
        if (!deletedAssetIdsRef.current.has(newAsset.id)) {
          switchAssetItemRef.current(newAsset.id);
        }
      } catch (error) {
        console.error('Error fetching new canvas asset:', error);
      }
    },
    [getAccessTokenSilently],
  );

  // Initial fetch when thread changes
  // Note: Only depends on effectiveThreadId, not fetchCanvasAssets, to avoid infinite loops
  useEffect(() => {
    if (!effectiveThreadId) return;
    fetchCanvasAssets();
  }, [effectiveThreadId]);

  // Handle canvas asset removed event
  const handleCanvasAssetRemoved = useCallback(
    (threadId: string, data: { canvasAssetId: string }) => {
      // Only handle if this is for our thread
      if (threadId !== effectiveThreadIdRef.current) {
        return;
      }

      console.log('Canvas asset removed:', data.canvasAssetId);

      // Remove the asset from the list
      setAssets((prevAssets) => {
        const filtered = prevAssets.filter(
          (asset) => asset.id !== data.canvasAssetId,
        );

        // If the removed asset was selected, switch to the first available asset
        // Use refs to always get latest values
        if (
          selectedAssetIdRef.current === data.canvasAssetId &&
          filtered.length > 0
        ) {
          switchAssetItemRef.current(filtered[0].id);
        }

        return filtered;
      });
    },
    [],
  );

  // Fetch comments when selected asset changes
  useEffect(() => {
    if (selectedAssetId) {
      fetchCommentsForAsset(selectedAssetId);
    }
  }, [selectedAssetId, fetchCommentsForAsset]);

  // Confetti handler
  const handleConfetti = useCallback(async (threadId: string, data: any) => {
    const confetti = (await import('canvas-confetti')).default;
    confetti({
      particleCount: 400,
      spread: 170,
      origin: { y: 0.6 },
    });
  }, []);

  // Register and unregister callbacks
  useEffect(() => {
    if (!effectiveThreadId) return;

    // Register callback for new canvas assets using unique ID
    registerCanvasAssetCreatedCallback(
      callbackIdRef.current,
      handleCanvasAssetCreated,
    );

    // Register callback for removed canvas assets
    registerCanvasAssetRemovedCallback(
      callbackIdRef.current,
      handleCanvasAssetRemoved,
    );

    // Register callback for confetti
    registerConfettiCallback(callbackIdRef.current, handleConfetti);

    return () => {
      unregisterCanvasAssetCreatedCallback(callbackIdRef.current);
      unregisterCanvasAssetRemovedCallback(callbackIdRef.current);
      unregisterConfettiCallback(callbackIdRef.current);
    };
    // Note: Intentionally excluding callback functions from dependencies
    // They use refs internally for thread safety, and including them causes
    // excessive re-registration when state changes (e.g., selectedAssetId)
  }, [
    effectiveThreadId,
    registerCanvasAssetCreatedCallback,
    unregisterCanvasAssetCreatedCallback,
    registerCanvasAssetRemovedCallback,
    unregisterCanvasAssetRemovedCallback,
    registerConfettiCallback,
    unregisterConfettiCallback,
  ]);

  // Get the currently selected asset
  const selectedAsset = assets.find((asset) => asset.id === selectedAssetId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get the title of the currently selected asset or default to "Pinboard"
  const getCurrentTitle = () => {
    if (loading) return 'Loading...';
    if (assets.length === 0) return 'Pinboard';
    if (!selectedAssetId) return 'Pinboard';

    const current = assets.find((asset) => asset.id === selectedAssetId);
    return current?.title || `Asset ${selectedAssetId.substring(0, 8)}`;
  };

  return (
    <div
      className={`canvas-assets${!hasAppTabs ? ' just-backdrop-only' : ''}`}
      ref={canvasAssetsRef}
    >
      {showHeader && (
        <div className='canvas-assets-header' ref={dropdownRef}>
          {viewMode === 'dreambox' ? (
            <div className='canvas-assets-title'>
              <PinboardIcon />
              <span>Dreambox</span>
            </div>
          ) : isAppLayout && appLayoutConfig ? (
            // App layout: tabs from config or dynamic tabs from component
            <div className='tabs app-layout'>
              <div className='tabs-inner'>
                <CollapsedRevealer discussingObject={discussingObject} />
                {effectiveTabs.map((tab) => {
                  const activeAppTab =
                    searchParams.get('app_tab') ||
                    effectiveTabs[0]?.id ||
                    'overview';
                  return (
                    <div
                      key={tab.id}
                      className={`tab ${activeAppTab === tab.id ? 'active' : ''}`}
                      onClick={() => {
                        setSearchParams(
                          (prev) => {
                            const newParams = new URLSearchParams(prev);
                            newParams.set('app_tab', tab.id);
                            return newParams;
                          },
                          { replace: true },
                        );
                      }}
                    >
                      <div className='tab-content'>
                        {tab.id === 'overview' && <CampaignIcon />}
                        <span className='tab-text'>{tab.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // Tab layout: dynamic canvas asset tabs
            <div className='tabs'>
              <div className='tabs-inner'>
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    ref={(el) => {
                      tabsRef.current[asset.id] = el;
                    }}
                    className={`tab ${
                      asset.id === selectedAssetId ? 'active' : ''
                    }`}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        visible: true,
                        x: e.clientX,
                        y: e.clientY,
                        assetId: asset.id,
                      });
                    }}
                  >
                    <div
                      className='tab-content'
                      onClick={() => {
                        if (asset.id !== selectedAssetId) {
                          switchAssetItem(asset.id);
                        }
                      }}
                    >
                      {asset.title === 'Campaign overview' && <CampaignIcon />}
                      <span className='tab-text'>
                        {asset.title || `Asset ${asset.id.substring(0, 8)}`}
                      </span>
                    </div>
                    {!asset.pinned && (
                      <div
                        className='tab-close'
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCanvasAssets([asset.id]);
                        }}
                        aria-label='Close tab'
                      >
                        <TabCloseIcon />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* <div className='switcher'>
            <ViewSwitcher
              viewMode={viewMode}
              onToggle={() =>
                setViewMode(viewMode === 'canvas' ? 'dreambox' : 'canvas')
              }
              hasNewCanvasAssets={hasNewCanvasAssets}
            />
          </div> */}
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.visible && contextMenu.assetId && (
        <div
          ref={contextMenuRef}
          className='tab-context-menu'
          style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            zIndex: 1000,
          }}
        >
          <div
            className='context-menu-item'
            onClick={() => {
              if (
                contextMenu.assetId &&
                contextMenu.assetId !== selectedAssetId
              ) {
                switchAssetItem(contextMenu.assetId);
              }
              setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
            }}
          >
            Open Tab
          </div>
          {(() => {
            const asset = assets.find((a) => a.id === contextMenu.assetId);
            return !asset?.pinned ? (
              <div
                className='context-menu-item'
                onClick={() => {
                  if (contextMenu.assetId) {
                    deleteCanvasAssets([contextMenu.assetId]);
                  }
                  setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
                }}
              >
                Close Tab
              </div>
            ) : null;
          })()}

          <div className='context-menu-divider' />
          <div
            className='context-menu-item'
            onClick={() => {
              const currentIndex = assets.findIndex(
                (a) => a.id === contextMenu.assetId,
              );
              const otherAssets = assets.filter(
                (a, idx) => idx !== currentIndex && !a.pinned,
              );
              deleteCanvasAssets(otherAssets.map((a) => a.id));
              setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
            }}
          >
            Close Other Tabs
          </div>
          <div
            className='context-menu-item'
            onClick={() => {
              const currentIndex = assets.findIndex(
                (a) => a.id === contextMenu.assetId,
              );
              const tabsToRight = assets
                .slice(currentIndex + 1)
                .filter((a) => !a.pinned);
              deleteCanvasAssets(tabsToRight.map((a) => a.id));
              setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
            }}
          >
            Close Tabs to the Right
          </div>
        </div>
      )}

      <div className='canvas-assets-content' ref={canvasContentRef}>
        {isAppLayout && appLayoutConfig && discussingObjectMatch
          ? // App layout: render component based on config
            appLayoutConfig.renderComponent({
              tabId:
                searchParams.get('app_tab') ||
                effectiveTabs[0]?.id ||
                'overview',
              threadId: effectiveThreadId || undefined,
              discussingObjectMatch,
              updateThreadViewVisualDescription,
              onTabsReady: handleTabsReady,
            })
          : isAppLayout
            ? null
            : viewMode === 'canvas' // Default app layout: don't render anything, only use modal
              ? assets.length === 0
                ? null
                : selectedAsset &&
                  selectedAsset.components && (
                    <div
                      className={`canvas-asset-wrapper ${isAnimating ? 'animating' : ''} ${animationStage}`}
                      style={{ position: 'relative' }}
                    >
                      {selectedAsset.components.map(
                        (component: CanvasAssetComponent, index: number) => {
                          // Map the component to a UIDSLComponent
                          const uiComponent: UIDSLComponent = {
                            type: component.component_type as UIDSLComponentType,
                            id: component.id,
                            props: {
                              ...component.props,
                              componentId: component.id,
                            } as unknown as UIDSLComponentProps,
                          };

                          return (
                            <UIUpdate
                              component={uiComponent}
                              viewId={selectedAsset.id}
                              threadId={effectiveThreadId}
                              section='canvas-assets'
                              onUserInput={() => {}}
                              updateThreadViewVisualDescription={
                                updateThreadViewVisualDescription
                              }
                              key={`${selectedAsset.id}-component-${index}`}
                            />
                          );
                        },
                      )}

                      {/* Asset-level comments overlay */}
                      {selectedAssetId &&
                        comments.assetComments[selectedAssetId] && (
                          <Comments
                            comments={comments.assetComments[selectedAssetId]}
                            baseElementRef={
                              canvasContentRef as React.RefObject<HTMLElement>
                            }
                            targetType='asset'
                            targetId={selectedAssetId}
                          />
                        )}
                    </div>
                  )
              : null}
      </div>

      {/* App layout modal for canvas assets */}
      <AppLayoutAssetModal
        show={isAppLayout && assets.length > 0 && appLayoutModalOpen}
        asset={
          assets.length > 0
            ? [...assets].sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime(),
              )[0]
            : null
        }
        threadId={effectiveThreadId}
        onClose={() => {
          setAppLayoutModalOpen(false);
          deleteCanvasAssets(assets.map((a) => a.id));
          // Clear asset_id from URL
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('asset_id');
          setSearchParams(newParams, { replace: true });
        }}
        updateThreadViewVisualDescription={updateThreadViewVisualDescription}
        container={canvasAssetsRef.current?.parentElement}
      />
    </div>
  );
};

export default CanvasAssets;
